import { Badge, BodyText, Button, Separator } from "@/components/ui";
import AmazonIcon from "@/icons/amazon.svg";
import { formatPhoneNumber } from "@/lib/formatters";
import { Copy } from "lucide-react";
import { Fragment } from "react";
import {
  AlternativeDispensingOfferType,
  type AlternativeDispensingPharmacyLocation,
  useAlternativeDispensingPharmacies,
} from "../network/useAlternativeDispensingPharmacies";

import { useGetPatient } from "@/features/Patient/network/usePatient";

function CopyButton({ text }: { text: string }) {
  return (
    <Button
      type="button"
      variant="icon"
      size="snatched"
      className="opacity-0 group-hover:opacity-100 transition-opacity leading-none active:!opacity-50 cursor-pointer"
      onClick={async () => {
        try {
          await navigator?.clipboard?.writeText(text);
        } catch {}
      }}
    >
      <Copy size={12} className="stroke-tertiary -translate-y-px" />
    </Button>
  );
}

const OFFER_TYPE_LABELS: Record<AlternativeDispensingOfferType, string> = {
  [AlternativeDispensingOfferType.Cash]: "Cash",
  [AlternativeDispensingOfferType.InsuranceEstimate]: "Estimated\nInsurance",
  [AlternativeDispensingOfferType.Prime]: "Prime",
  [AlternativeDispensingOfferType.RxPass]: "RxPass",
};

interface AlternativePharmaciesProps {
  patientId: string;
  encounterId: string;
  medicationRequestId: string;
  currentPharmacyVendorId?: string;
  onSelectPharmacy: (
    pharmacy: AlternativeDispensingPharmacyLocation,
  ) => Promise<void>;
  isSaving?: boolean;
  readOnly?: boolean;
}

export function AlternativePharmacies({
  patientId,
  encounterId,
  medicationRequestId,
  currentPharmacyVendorId,
  onSelectPharmacy,
  isSaving,
  readOnly,
}: AlternativePharmaciesProps) {
  const { data: patient } = useGetPatient(patientId as string);
  const isAmazonPatient = Boolean(patient?.amazonId);

  const { data, isLoading: isLoadingAlternatives } =
    useAlternativeDispensingPharmacies({
      patientId,
      encounterId,
      medicationRequestId,
      enabled: isAmazonPatient,
    });

  const dispensingPharmacies = (
    data?.alternativeDispensingPharmacies ?? []
  ).filter(
    (p) =>
      p.isDrugAvailable &&
      !p.notDispensableReason &&
      p.pharmacy.isAmazonAffiliated,
  );

  if (isLoadingAlternatives || dispensingPharmacies.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <Separator />
      <div className="flex flex-col gap-2">
        <BodyText size="sm" textColor="secondary" bold>
          Alternative Pharmacies
        </BodyText>
        {dispensingPharmacies.map((dispensingPharmacy, i) => {
          const pharmacyLocation = dispensingPharmacy.pharmacy;
          if (!pharmacyLocation) return null;

          const isCurrent =
            currentPharmacyVendorId !== undefined &&
            (pharmacyLocation.pharmacyId.toString() ===
              currentPharmacyVendorId ||
              pharmacyLocation.affiliatedPharmacyIds.includes(
                Number.parseInt(currentPharmacyVendorId),
              ));

          return (
            <div
              key={pharmacyLocation.pharmacyId ?? i}
              className="flex flex-col gap-1 px-3 py-2 rounded-md border overflow-hidden border-primary bg-white"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-col gap-0 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {pharmacyLocation.isAmazonAffiliated && (
                      <AmazonIcon className="w-3 h-3" />
                    )}
                    <BodyText size="sm" bold>
                      {pharmacyLocation.pharmacyName}
                    </BodyText>
                  </div>
                  <BodyText size="xs" textColor="secondary">
                    {pharmacyLocation.addressLine_1}, {pharmacyLocation.city},{" "}
                    {pharmacyLocation.state} {pharmacyLocation.zipCode} ·{" "}
                    {formatPhoneNumber(pharmacyLocation.primaryPhone)}
                  </BodyText>
                </div>
                {isCurrent && !readOnly && (
                  <Badge variant="success" className="shrink-0">
                    <BodyText size="xs" bold>
                      Selected
                    </BodyText>
                  </Badge>
                )}
                {!isCurrent && !readOnly && (
                  <Button
                    size="xs"
                    variant="secondary"
                    className="shrink-0"
                    disabled={isSaving}
                    onClick={() => onSelectPharmacy(pharmacyLocation)}
                  >
                    Switch
                  </Button>
                )}
              </div>

              {dispensingPharmacy.pharmacyOffers &&
                dispensingPharmacy.pharmacyOffers.length > 0 && (
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 pl-2 border-l-2 border-link">
                    {dispensingPharmacy.pharmacyOffers.map((offer) => {
                      const formattedPrice =
                        offer.price != null
                          ? `$${offer.price.trim().replace(/^\$/, "")}`
                          : null;

                      return (
                        <Fragment
                          key={`${offer.offerType}-${offer.quantity}-${offer.price}`}
                        >
                          <BodyText
                            size="sm"
                            bold
                            textColor="link"
                            className="whitespace-pre-line"
                          >
                            {OFFER_TYPE_LABELS[offer.offerType]}
                          </BodyText>
                          <div className="flex flex-col">
                            {(offer.quantity || formattedPrice) && (
                              <div className="group flex items-center gap-1">
                                <BodyText size="sm" textColor="secondary">
                                  {[offer.quantity, formattedPrice]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </BodyText>
                                {formattedPrice != null && (
                                  <CopyButton text={formattedPrice} />
                                )}
                              </div>
                            )}
                            {offer.estimatedDelivery && (
                              <div className="group flex items-center gap-1">
                                <BodyText size="sm" textColor="tertiary">
                                  {offer.estimatedDelivery}
                                </BodyText>
                                <CopyButton text={offer.estimatedDelivery} />
                              </div>
                            )}
                          </div>
                        </Fragment>
                      );
                    })}
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
